import { type ApiError, type Locale } from '@sjuni/types';

/**
 * The one fetch wrapper. Every request goes through it so the headers in
 * docs/04-API-SPEC.md §1 and the error envelope in §1 are handled once.
 */

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '/api/v1';

/*
 * The bearer token and the "this token is dead" signal are set once by the auth
 * provider rather than threaded through every call site.
 *
 * A module-level holder rather than a parameter because the alternative is
 * every hook in the dashboard taking a token it does not otherwise care about,
 * and one of them eventually forgetting to. There is exactly one session per
 * tab, so there is nothing here for a second one to collide with.
 */
let bearerToken: string | null = null;
let onUnauthorized: (() => void) | null = null;

export function setBearerToken(token: string | null): void {
  bearerToken = token;
}

/**
 * Called for any 401 from an authenticated request. docs/05-ADMIN-DASHBOARD.md
 * §6.7 wants a re-auth modal that preserves unsaved form state, not a redirect
 * to the login screen — so this is a notification, not a navigation.
 */
export function setUnauthorizedHandler(handler: (() => void) | null): void {
  onUnauthorized = handler;
}

/** Thrown for any non-2xx response, carrying the API's own error envelope. */
export class ApiRequestError extends Error {
  constructor(
    readonly status: number,
    readonly payload: ApiError,
  ) {
    super(payload.message);
    this.name = 'ApiRequestError';
  }

  /** 422 — field-level validation messages, keyed by field name. */
  get validationErrors(): Record<string, string[]> {
    return this.payload.errors ?? {};
  }
}

export interface RequestOptions extends Omit<RequestInit, 'body'> {
  /** docs/00-PROJECT-BRIEF.md §5.6 — sent as `?locale=`, which wins over `Accept-Language`. */
  locale?: Locale;
  query?: Record<string, string | number | boolean | undefined | null>;
  body?: unknown;
  /**
   * Set on the auth endpoints themselves. A 401 from `login` means the password
   * was wrong; a 401 from anywhere else means the session died, and only the
   * second should raise the re-auth modal.
   */
  anonymous?: boolean;
}

/**
 * The locale the *server's* messages should come back in.
 *
 * Every validation message the API produces is translated server-side, so a
 * request that does not say which locale it wants gets the API's default. The
 * sign-in screen showed "These credentials do not match our records." under a
 * Georgian label because of it — and there is no user yet at that point, so
 * `users.locale` cannot answer. The admin chrome's own language is the honest
 * substitute, and once someone is signed in the two agree.
 */
let uiLocale: () => Locale = () => 'ka';

export function setLocaleSource(source: () => Locale): void {
  uiLocale = source;
}

export async function apiFetch<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { locale = uiLocale(), query, body, headers, anonymous, ...init } = options;

  const url = new URL(`${BASE_URL}${path}`, window.location.origin);

  for (const [key, value] of Object.entries(query ?? {})) {
    if (value !== undefined && value !== null) {
      url.searchParams.set(key, String(value));
    }
  }

  if (locale) {
    url.searchParams.set('locale', locale);
  }

  /*
   * A `FormData` body goes as-is, and without a `Content-Type` of our own.
   * §5.4's document upload is multipart, and the boundary is generated by the
   * browser when it serialises the body — setting the header here would send a
   * `multipart/form-data` with no boundary, which every server parses as an
   * empty request. It is a header you have to *not* set.
   */
  const isMultipart = body instanceof FormData;

  const response = await fetch(url, {
    ...init,
    headers: {
      Accept: 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
      ...(body === undefined || isMultipart ? {} : { 'Content-Type': 'application/json' }),
      ...(bearerToken === null ? {} : { Authorization: `Bearer ${bearerToken}` }),
      ...headers,
    },
    ...(body === undefined ? {} : { body: isMultipart ? body : JSON.stringify(body) }),
  });

  if (!response.ok) {
    if (response.status === 401 && anonymous !== true) {
      onUnauthorized?.();
    }

    throw new ApiRequestError(response.status, await readError(response));
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return (await response.json()) as T;
}

/**
 * Downloads a file the API serves behind the bearer token.
 *
 * A plain `<a href>` cannot do this: the admin API authenticates with an
 * `Authorization` header rather than a cookie, so the browser's own navigation
 * would arrive unauthenticated and get a 401 — which, being a navigation,
 * would replace the admin with a JSON error page.
 *
 * So the file is fetched with the token like any other request and handed to
 * the browser as a blob. The object URL is revoked immediately afterwards:
 * it pins the whole file in memory until it is, and docs/05-ADMIN-DASHBOARD.md
 * §5.7's export of a two-year inbox is not small.
 */
export async function apiDownload(
  path: string,
  query: RequestOptions['query'] = {},
  fallbackFilename = 'download',
): Promise<void> {
  const response = await apiFetchRaw(path, query);

  const blob = await response.blob();
  const url = URL.createObjectURL(blob);

  const anchor = document.createElement('a');

  anchor.href = url;
  // The server's own filename, which carries the date, when it sent one.
  anchor.download = filenameFrom(response) ?? fallbackFilename;

  document.body.append(anchor);
  anchor.click();
  anchor.remove();

  URL.revokeObjectURL(url);
}

async function apiFetchRaw(path: string, query: RequestOptions['query']): Promise<Response> {
  const url = new URL(`${BASE_URL}${path}`, window.location.origin);

  for (const [key, value] of Object.entries(query ?? {})) {
    if (value !== undefined && value !== null) {
      url.searchParams.set(key, String(value));
    }
  }

  url.searchParams.set('locale', uiLocale());

  const response = await fetch(url, {
    headers: {
      'X-Requested-With': 'XMLHttpRequest',
      ...(bearerToken === null ? {} : { Authorization: `Bearer ${bearerToken}` }),
    },
  });

  if (!response.ok) {
    if (response.status === 401) {
      onUnauthorized?.();
    }

    throw new ApiRequestError(response.status, await readError(response));
  }

  return response;
}

/** `attachment; filename=submissions-2026-08-15.csv` → the filename. */
function filenameFrom(response: Response): string | null {
  const disposition = response.headers.get('content-disposition');
  const match = disposition === null ? null : /filename="?([^";]+)"?/.exec(disposition);

  return match?.[1] ?? null;
}

async function readError(response: Response): Promise<ApiError> {
  try {
    const payload: unknown = await response.json();

    if (typeof payload === 'object' && payload !== null && 'message' in payload) {
      return payload as ApiError;
    }
  } catch {
    // A gateway or proxy failure can return HTML; fall through to the status.
  }

  return { message: `${response.status} ${response.statusText}`.trim() };
}
